'use client';

import * as React from 'react';
import {useState, useEffect} from 'react';
import {X, Settings, Check, ExternalLink} from 'lucide-react';
import {getCookie, setCookie, deleteCookie} from 'cookies-next';
import {Button} from '../button/button';
import {Card} from '../card/card';
import {Switch} from '../switch/switch';
import {Separator} from '../separator/separator';

export interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

export interface CookieBannerProps {
  onAcceptAll?: (preferences: CookiePreferences) => void;
  onAcceptSelected?: (preferences: CookiePreferences) => void;
  onDecline?: () => void;
  showSettings?: boolean;
  className?: string;
  position?: 'bottom' | 'top';
  locale?: 'en' | 'no' | 'sv' | 'da';
  title?: string;
  description?: string;
  acceptAllText?: string;
  acceptSelectedText?: string;
  declineText?: string;
  settingsText?: string;
  learnMoreText?: string;
  necessaryText?: string;
  analyticsText?: string;
  marketingText?: string;
  necessaryDescription?: string;
  analyticsDescription?: string;
  marketingDescription?: string;
  cookiePolicyUrl?: string;
}

const defaultPreferences: CookiePreferences = {
  necessary: true,
  analytics: false,
  marketing: false
};

const COOKIE_CONSENT_KEY = 'cookie-consent';
const COOKIE_CONSENT_OPTIONS = {
  maxAge: 365 * 24 * 60 * 60, // 1 year
  path: '/',
  sameSite: 'lax' as const,
  secure: process.env.NODE_ENV === 'production'
};

// Multilingual content for Rybbit Analytics
const analyticsContent = {
  en: {
    short:
      'We use Rybbit Analytics to understand how you use our platform and improve your experience. This privacy-focused analytics tool helps us make data-driven decisions while respecting your privacy.',
    detailed:
      'What is Rybbit Analytics? Rybbit is a privacy-first analytics platform that helps us understand how visitors interact with our website and applications. Unlike traditional analytics tools, Rybbit is designed with privacy at its core and complies with GDPR without requiring cookie consent for basic usage tracking.\n\nWhat data does Rybbit collect?\n• Page views and navigation patterns\n• Device type and browser information (anonymized)\n• Geographic location (country/region level only)\n• Time spent on pages and user interactions\n• Referral sources (how you found our site)\n\nWhat data does Rybbit NOT collect?\n• Personal identifiable information (PII)\n• Email addresses or contact details\n• Individual user profiles or tracking across sessions\n• Detailed location data (IP addresses are anonymized)\n• Cross-site tracking or behavioral profiling\n\nHow we use this data:\n• Improve website performance and user experience\n• Understand which features are most valuable to our users\n• Optimize our platform based on real usage patterns\n• Make informed decisions about product development\n\nData retention and hosting: Analytics data is retained for 24 months and then automatically deleted. All data is processed and stored on our own dedicated Rybbit installation running on our AWS tenant in Stockholm, Sweden. This means your data never leaves our secure infrastructure and remains within the EU at all times.\n\nYour privacy rights: You can opt-out of analytics tracking at any time through our privacy settings. This will not affect your ability to use our platform.'
  },
  no: {
    short:
      'Vi bruker Rybbit Analytics for å forstå hvordan du bruker plattformen vår og forbedre din opplevelse. Dette personvernfokuserte analyseverktøyet hjelper oss med å ta datadrevne beslutninger samtidig som vi respekterer ditt personvern.',
    detailed:
      'Hva er Rybbit Analytics? Rybbit er en personvernvennlig analyseplattform som hjelper oss å forstå hvordan besøkende interagerer med nettsiden og applikasjonene våre. I motsetning til tradisjonelle analyseverktøy er Rybbit designet med personvern i kjernen og overholder GDPR uten å kreve cookie-samtykke for grunnleggende brukssporing.\n\nHvilke data samler Rybbit inn?\n• Sidevisninger og navigasjonsmønstre\n• Enhetstype og nettleserinformasjon (anonymisert)\n• Geografisk plassering (kun land/region-nivå)\n• Tid brukt på sider og brukerinteraksjoner\n• Henvisningskilder (hvordan du fant siden vår)\n\nHvilke data samler Rybbit IKKE inn?\n• Personlig identifiserbar informasjon (PII)\n• E-postadresser eller kontaktdetaljer\n• Individuelle brukerprofiler eller sporing på tvers av økter\n• Detaljerte stedsdata (IP-adresser anonymiseres)\n• Krysside-sporing eller atferdsprofiling\n\nHvordan vi bruker disse dataene:\n• Forbedre nettstedets ytelse og brukeropplevelse\n• Forstå hvilke funksjoner som er mest verdifulle for våre brukere\n• Optimalisere plattformen vår basert på faktiske bruksmønstre\n• Ta informerte beslutninger om produktutvikling\n\nDatalagring og hosting: Analysedata lagres i 24 måneder og slettes deretter automatisk. All data prosesseres og lagres på vår egen dedikerte Rybbit-installasjon som kjører på vår AWS-tenant i Stockholm, Sverige. Dette betyr at dataene dine aldri forlater vår sikre infrastruktur og forblir innenfor EU til enhver tid.\n\nDine personvernrettigheter: Du kan melde deg ut av analysesporing når som helst gjennom våre personverninstillinger. Dette vil ikke påvirke din mulighet til å bruke plattformen vår.'
  },
  sv: {
    short:
      'Vi använder Rybbit Analytics för att förstå hur du använder vår plattform och förbättra din upplevelse. Detta integritetsfokuserade analysverktyg hjälper oss att fatta datadrivna beslut samtidigt som vi respekterar din integritet.',
    detailed:
      'Vad är Rybbit Analytics? Rybbit är en integritetsvänlig analysplattform som hjälper oss att förstå hur besökare interagerar med vår webbplats och applikationer. Till skillnad från traditionella analysverktyg är Rybbit designad med integritet i kärnan och följer GDPR utan att kräva cookie-samtycke för grundläggande användningsspårning.\n\nVilken data samlar Rybbit in?\n• Sidvisningar och navigationsmönster\n• Enhetstyp och webbläsarinformation (anonymiserad)\n• Geografisk plats (endast land/region-nivå)\n• Tid spenderad på sidor och användarinteraktioner\n• Hänvisningskällor (hur du hittade vår sida)\n\nVilken data samlar Rybbit INTE in?\n• Personligt identifierbar information (PII)\n• E-postadresser eller kontaktuppgifter\n• Individuella användarprofiler eller spårning över sessioner\n• Detaljerad platsdata (IP-adresser anonymiseras)\n• Korssidesspårning eller beteendeprofilering\n\nHur vi använder denna data:\n• Förbättra webbplatsens prestanda och användarupplevelse\n• Förstå vilka funktioner som är mest värdefulla för våra användare\n• Optimera vår plattform baserat på verkliga användningsmönster\n• Fatta välgrundade beslut om produktutveckling\n\nDatalagring och hosting: Analysdata lagras i 24 månader och raderas sedan automatiskt. All data bearbetas och lagras på vår egen dedikerade Rybbit-installation som körs på vår AWS-tenant i Stockholm, Sverige. Detta innebär att din data aldrig lämnar vår säkra infrastruktur och förblir inom EU hela tiden.\n\nDina integritetsrättigheter: Du kan välja bort analysesporing när som helst genom våra integritetsinställningar. Detta kommer inte att påverka din möjlighet att använda vår plattform.'
  },
  da: {
    short:
      'Vi bruger Rybbit Analytics til at forstå, hvordan du bruger vores platform og forbedre din oplevelse. Dette privatlivsfokuserede analyseværktøj hjælper os med at træffe datadrevne beslutninger, samtidig med at vi respekterer dit privatliv.',
    detailed:
      'Hvad er Rybbit Analytics? Rybbit er en privatlivsvenlig analyseplatform, der hjælper os med at forstå, hvordan besøgende interagerer med vores hjemmeside og applikationer. I modsætning til traditionelle analyseværktøjer er Rybbit designet med privatliv i kernen og overholder GDPR uden at kræve cookie-samtykke til grundlæggende brugeropsporing.\n\nHvilke data indsamler Rybbit?\n• Sidevisninger og navigationsmønstre\n• Enhedstype og browserinformation (anonymiseret)\n• Geografisk placering (kun land/region-niveau)\n• Tid brugt på sider og brugerinteraktioner\n• Henvisningskilder (hvordan du fandt vores side)\n\nHvilke data indsamler Rybbit IKKE?\n• Personligt identificerbar information (PII)\n• E-mailadresser eller kontaktoplysninger\n• Individuelle brugerprofiler eller sporing på tværs af sessioner\n• Detaljerede lokationsdata (IP-adresser anonymiseres)\n• Tværside-sporing eller adfærdsprofiling\n\nHvordan vi bruger disse data:\n• Forbedre hjemmesidens ydeevne og brugeroplevelse\n• Forstå hvilke funktioner, der er mest værdifulde for vores brugere\n• Optimere vores platform baseret på faktiske brugsmønstre\n• Træffe informerede beslutninger om produktudvikling\n\nDataopbevaring og hosting: Analysedata opbevares i 24 måneder og slettes derefter automatisk. Alle data behandles og gemmes på vores egen dedikerede Rybbit-installation, der kører på vores AWS-tenant i Stockholm, Sverige. Dette betyder, at dine data aldrig forlader vores sikre infrastruktur og forbliver inden for EU til enhver tid.\n\nDine privatlivsrettigheder: Du kan fravælge analysesporing til enhver tid gennem vores privatlivsindstillinger. Dette vil ikke påvirke din mulighed for at bruge vores platform.'
  }
};

// Default multilingual content
const defaultContent = {
  en: {
    title: 'We use cookies',
    description:
      'We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. By clicking "Accept All", you consent to our use of cookies.',
    acceptAllText: 'Accept All',
    acceptSelectedText: 'Accept Selected',
    declineText: 'Decline',
    settingsText: 'Settings',
    learnMoreText: 'Learn More',
    necessaryText: 'Strictly Necessary',
    analyticsText: 'Analytics & Performance',
    marketingText: 'Marketing & Advertising',
    necessaryDescription:
      'These cookies are essential for the website to function properly.',
    marketingDescription:
      'These cookies are used to deliver personalized advertisements.'
  },
  no: {
    title: 'Vi bruker cookies',
    description:
      'Vi bruker cookies for å forbedre din nettleseropplevelse, levere personlig tilpasset innhold og analysere trafikken vår. Ved å klikke "Godta alle" samtykker du til vår bruk av cookies.',
    acceptAllText: 'Godta alle',
    acceptSelectedText: 'Godta valgte',
    declineText: 'Avslå',
    settingsText: 'Innstillinger',
    learnMoreText: 'Lær mer',
    necessaryText: 'Strengt nødvendig',
    analyticsText: 'Analyse & ytelse',
    marketingText: 'Markedsføring & reklame',
    necessaryDescription:
      'Disse cookies er nødvendige for at nettsiden skal fungere ordentlig.',
    marketingDescription:
      'Disse cookies brukes til å levere personlig tilpassede annonser.'
  },
  sv: {
    title: 'Vi använder cookies',
    description:
      'Vi använder cookies för att förbättra din webbläsarupplevelse, leverera personlig anpassat innehåll och analysera vår trafik. Genom att klicka "Acceptera alla" samtycker du till vår användning av cookies.',
    acceptAllText: 'Acceptera alla',
    acceptSelectedText: 'Acceptera valda',
    declineText: 'Avböj',
    settingsText: 'Inställningar',
    learnMoreText: 'Läs mer',
    necessaryText: 'Strikt nödvändig',
    analyticsText: 'Analys & prestanda',
    marketingText: 'Marknadsföring & reklam',
    necessaryDescription:
      'Dessa cookies är nödvändiga för att webbplatsen ska fungera ordentligt.',
    marketingDescription:
      'Dessa cookies används för att leverera personlig anpassade annonser.'
  },
  da: {
    title: 'Vi bruger cookies',
    description:
      'Vi bruger cookies til at forbedre din browseroplevelse, levere personligt tilpasset indhold og analysere vores trafik. Ved at klikke "Accepter alle" samtykker du til vores brug af cookies.',
    acceptAllText: 'Accepter alle',
    acceptSelectedText: 'Accepter valgte',
    declineText: 'Afvis',
    settingsText: 'Indstillinger',
    learnMoreText: 'Lær mere',
    necessaryText: 'Strengt nødvendig',
    analyticsText: 'Analyse & ydeevne',
    marketingText: 'Markedsføring & reklame',
    necessaryDescription:
      'Disse cookies er nødvendige for, at hjemmesiden skal fungere ordentligt.',
    marketingDescription:
      'Disse cookies bruges til at levere personligt tilpassede annoncer.'
  }
};

export function CookieBanner({
  onAcceptAll,
  onAcceptSelected,
  onDecline,
  showSettings = true,
  className = '',
  position = 'bottom',
  locale = 'no',
  title,
  description,
  acceptAllText,
  acceptSelectedText,
  declineText,
  settingsText,
  learnMoreText,
  necessaryText,
  analyticsText,
  marketingText,
  necessaryDescription,
  analyticsDescription,
  marketingDescription,
  cookiePolicyUrl
}: CookieBannerProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);
  const [preferences, setPreferences] =
    useState<CookiePreferences>(defaultPreferences);

  // Get localized content
  const content = defaultContent[locale];
  const rybbitContent = analyticsContent[locale];

  // Use provided props or fall back to localized defaults
  const finalTitle = title || content.title;
  const finalDescription = description || content.description;
  const finalAcceptAllText = acceptAllText || content.acceptAllText;
  const finalAcceptSelectedText =
    acceptSelectedText || content.acceptSelectedText;
  const finalDeclineText = declineText || content.declineText;
  const finalSettingsText = settingsText || content.settingsText;
  const finalLearnMoreText = learnMoreText || content.learnMoreText;
  const finalNecessaryText = necessaryText || content.necessaryText;
  const finalAnalyticsText = analyticsText || content.analyticsText;
  const finalMarketingText = marketingText || content.marketingText;
  const finalNecessaryDescription =
    necessaryDescription || content.necessaryDescription;
  const finalAnalyticsDescription = analyticsDescription || rybbitContent.short;
  const finalMarketingDescription =
    marketingDescription || content.marketingDescription;

  // Generate cookie policy URL if not provided
  const finalCookiePolicyUrl = cookiePolicyUrl || `/${locale}/cookie-policy`;

  useEffect(() => {
    // Check if user has already made a choice using cookies
    const hasConsent = getCookie(COOKIE_CONSENT_KEY);
    if (!hasConsent) {
      setIsVisible(true);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      necessary: true,
      analytics: true,
      marketing: true
    };

    setCookie(
      COOKIE_CONSENT_KEY,
      JSON.stringify(allAccepted),
      COOKIE_CONSENT_OPTIONS
    );
    setIsVisible(false);

    window.dispatchEvent(new CustomEvent('cookieConsentChanged'));

    onAcceptAll?.(allAccepted);
  };

  const handleAcceptSelected = () => {
    setCookie(
      COOKIE_CONSENT_KEY,
      JSON.stringify(preferences),
      COOKIE_CONSENT_OPTIONS
    );
    setIsVisible(false);
    setShowSettingsPanel(false);

    window.dispatchEvent(new CustomEvent('cookieConsentChanged'));

    onAcceptSelected?.(preferences);
  };

  const handleDecline = () => {
    const declined: CookiePreferences = {
      necessary: true,
      analytics: false,
      marketing: false
    };

    setCookie(
      COOKIE_CONSENT_KEY,
      JSON.stringify(declined),
      COOKIE_CONSENT_OPTIONS
    );
    setIsVisible(false);

    window.dispatchEvent(new CustomEvent('cookieConsentChanged'));

    onDecline?.();
  };

  const handlePreferenceChange = (
    key: keyof CookiePreferences,
    value: boolean
  ) => {
    setPreferences(prev => ({
      ...prev,
      [key]: value
    }));
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className={`fixed ${
        position === 'bottom' ? 'bottom-0' : 'top-0'
      } bg-background/95 supports-[backdrop-filter]:bg-background/60 border-border right-0 left-0 z-50 border-t p-4 backdrop-blur ${
        position === 'top' ? 'border-b' : 'border-t'
      } ${className}`}
    >
      <Card className="mx-auto max-w-4xl">
        <div className="p-6">
          {!showSettingsPanel ? (
            // Main banner view
            <div className="space-y-4">
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold">{finalTitle}</h3>
                  <p className="text-muted-foreground text-sm">
                    {finalDescription}
                  </p>
                  {/* Learn More Link */}
                  <a
                    href={finalCookiePolicyUrl}
                    className="text-primary inline-flex items-center text-sm hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {finalLearnMoreText}
                    <ExternalLink className="ml-1 h-3 w-3" />
                  </a>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsVisible(false)}
                  className="h-8 w-8 p-0"
                  aria-label="Lukk"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row">
                <Button
                  onClick={handleAcceptAll}
                  className="flex-1"
                  aria-label={finalAcceptAllText}
                >
                  {finalAcceptAllText}
                </Button>
                {showSettings && (
                  <Button
                    variant="outline"
                    onClick={() => setShowSettingsPanel(true)}
                    className="flex-1"
                    aria-label={finalSettingsText}
                  >
                    <Settings className="mr-2 h-4 w-4" />
                    {finalSettingsText}
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={handleDecline}
                  className="flex-1"
                  aria-label={finalDeclineText}
                >
                  {finalDeclineText}
                </Button>
              </div>
            </div>
          ) : (
            // Settings panel view
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">{finalSettingsText}</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowSettingsPanel(false)}
                  className="h-8 w-8 p-0"
                  aria-label="Lukk"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="space-y-4">
                {/* Necessary Cookies */}
                <div className="flex items-start justify-between space-x-4">
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center space-x-2">
                      <h4 className="font-medium">{finalNecessaryText}</h4>
                      <span className="bg-primary/10 text-primary rounded px-2 py-1 text-xs">
                        {locale === 'no'
                          ? 'Påkrevd'
                          : locale === 'en'
                            ? 'Required'
                            : locale === 'sv'
                              ? 'Krävs'
                              : 'Påkrævet'}
                      </span>
                    </div>
                    <p className="text-muted-foreground text-sm">
                      {finalNecessaryDescription}
                    </p>
                  </div>
                  <Switch
                    checked={preferences.necessary}
                    disabled
                    className="mt-1"
                  />
                </div>

                <Separator />

                {/* Analytics Cookies */}
                <div className="flex items-start justify-between space-x-4">
                  <div className="flex-1 space-y-1">
                    <h4 className="font-medium">{finalAnalyticsText}</h4>
                    <p className="text-muted-foreground text-sm">
                      {finalAnalyticsDescription}
                    </p>
                  </div>
                  <Switch
                    checked={preferences.analytics}
                    onCheckedChange={checked =>
                      handlePreferenceChange('analytics', checked)
                    }
                    className="mt-1"
                  />
                </div>

                <Separator />

                {/* Marketing Cookies */}
                <div className="flex items-start justify-between space-x-4">
                  <div className="flex-1 space-y-1">
                    <h4 className="font-medium">{finalMarketingText}</h4>
                    <p className="text-muted-foreground text-sm">
                      {finalMarketingDescription}
                    </p>
                  </div>
                  <Switch
                    checked={preferences.marketing}
                    onCheckedChange={checked =>
                      handlePreferenceChange('marketing', checked)
                    }
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2 pt-4 sm:flex-row">
                <Button
                  onClick={handleAcceptSelected}
                  className="flex-1"
                  aria-label={finalAcceptSelectedText}
                >
                  <Check className="mr-2 h-4 w-4" />
                  {finalAcceptSelectedText}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowSettingsPanel(false)}
                  className="flex-1"
                  aria-label="Tilbake"
                >
                  {locale === 'no'
                    ? 'Tilbake'
                    : locale === 'en'
                      ? 'Back'
                      : locale === 'sv'
                        ? 'Tillbaka'
                        : 'Tilbage'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
